"use client";

import React from "react";
import { CircleAlert, RotateCcw } from "lucide-react";

import { DeskEmpty } from "../desk/Desk";

type Props = {
  title: string;
  description?: string;
  retryLabel: string;
  /** Replaces the default fallback entirely. */
  fallback?: (args: { error: Error; reset: () => void }) => React.ReactNode;
  /** Re-throwing subtrees can be re-mounted by changing this. */
  resetKey?: unknown;
  onError?: (error: Error, info: React.ErrorInfo) => void;
  /** Shows `error.message` verbatim. Off by default: a raw message can leak internals. */
  showDetail?: boolean;
  children: React.ReactNode;
};

type State = { error: Error | null };

/**
 * A SUBTREE THAT THREW, in v2 — `components/shared/ErrorBoundary`'s rules,
 * one for one (same props, same `resetKey` recovery, same retry by re-mount),
 * with the v2 empty page in its failure tone as the fallback. The rest of the
 * page stays alive. ErrorBoundary keeps its legacy face for the routes not
 * yet migrated.
 */
export class SectionBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  private reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback({ error, reset: this.reset });
    const body = [this.props.description, this.props.showDetail ? error.message : null].filter(Boolean).join(" — ");
    return (
      <DeskEmpty
        tone="error"
        icon={<CircleAlert />}
        title={this.props.title}
        body={body || undefined}
        actions={<button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={this.reset}><RotateCcw aria-hidden />{this.props.retryLabel}</button>}
      />
    );
  }
}
